// Obstacle-avoiding orthogonal edge routing (issue #918, Phase 1).
//
// Pure geometry: given two endpoints and the node rectangles, produce an ordered
// list of INTERIOR waypoints (endpoints excluded — those are the live handle pins).
// The result is a rounded-orthogonal polyline; how it is stroked is a render-time
// concern that lives in the edge component, not here.
//
// Every node is an obstacle, including the edge's own source and target: the route
// leaves the source pin and enters the target pin via short outward stubs, then must
// route around all boxes. For a backward edge this reproduces the loopback arc for
// free — the route cannot cut through its own endpoint boxes, so it arcs around them.

export interface Point {
  x: number;
  y: number;
}

// Top-left origin, matching React Flow node positions.
export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface RouteParams {
  // Preferred clearance from boxes (a soft target, not a hard wall) and the exit/entry
  // stub length. The route keeps this distance when it's free to.
  margin: number;
  // Cost added per 90° turn; high values favour few-segment, straight-reading routes.
  bendPenalty: number;
  // Hard keep-out: the route never comes closer than this to a box.
  hardClearance: number;
  // Cost per pixel for running closer than `margin` to a box, scaled by how close (0 at
  // the preferred margin, max at the hard clearance). This is what lets A* weigh squeezing
  // through a tight gap against a cleaner detour, instead of always squeezing.
  proximityWeight: number;
  // Length of the perpendicular exit/entry nub at each handle. Only needs to clear the box
  // (> hardClearance); kept short so edges don't reserve a large stub off every handle.
  stubLength: number;
}

export interface RouteInput {
  source: Point;
  target: Point;
  // Unit vectors pointing outward from each node at its handle. In this editor source
  // handles are on the right ({x:1,y:0}) and target handles on the left ({x:-1,y:0}).
  sourceDir: Point;
  targetDir: Point;
  // All node rectangles, including the source and target nodes themselves.
  obstacles: Rect[];
}

export const DEFAULT_ROUTE_PARAMS: RouteParams = {
  margin: 16,
  bendPenalty: 1000,
  hardClearance: 1,
  proximityWeight: 40,
  stubLength: 8,
};

// Above this many visibility-graph cells, skip A* and fall back to the naive orthogonal
// path. Soft clearance adds lattice lines at both the hard and preferred offsets, so cell
// counts are higher than a single-margin lattice; local obstacle scoping (a later step)
// keeps real graphs well under this.
const MAX_GRID_CELLS = 40000;

const EPS = 0.01;

const expandRect = (r: Rect, m: number): Rect => ({ x: r.x - m, y: r.y - m, width: r.width + 2 * m, height: r.height + 2 * m });

const pointStrictlyInside = (p: Point, r: Rect): boolean => p.x > r.x + EPS && p.x < r.x + r.width - EPS && p.y > r.y + EPS && p.y < r.y + r.height - EPS;

// Whether an axis-aligned segment passes through a rect's interior. A segment running
// along the boundary (tangent) is not blocked — that is the hugging behaviour we want.
const orthSegmentBlocked = (a: Point, b: Point, r: Rect): boolean => {
  if (Math.abs(a.y - b.y) < EPS) {
    // horizontal
    const y = a.y;
    const xlo = Math.min(a.x, b.x);
    const xhi = Math.max(a.x, b.x);
    return y > r.y + EPS && y < r.y + r.height - EPS && xhi > r.x + EPS && xlo < r.x + r.width - EPS;
  }
  // vertical
  const x = a.x;
  const ylo = Math.min(a.y, b.y);
  const yhi = Math.max(a.y, b.y);
  return x > r.x + EPS && x < r.x + r.width - EPS && yhi > r.y + EPS && ylo < r.y + r.height - EPS;
};

const segmentClear = (a: Point, b: Point, rects: Rect[]): boolean => !rects.some((r) => orthSegmentBlocked(a, b, r));

// Smallest perpendicular distance from an axis-aligned segment to any obstacle it runs
// alongside (ranges overlap on the segment's axis). Infinity when nothing is alongside.
// Drives the soft proximity cost — measured against the raw boxes, not the expansion.
const clearanceAlong = (a: Point, b: Point, rects: Rect[]): number => {
  let min = Infinity;
  if (Math.abs(a.y - b.y) < EPS) {
    const y = a.y;
    const xlo = Math.min(a.x, b.x);
    const xhi = Math.max(a.x, b.x);
    for (const r of rects) {
      if (xhi > r.x + EPS && xlo < r.x + r.width - EPS) {
        const d = y <= r.y ? r.y - y : y >= r.y + r.height ? y - (r.y + r.height) : 0;
        if (d < min) min = d;
      }
    }
  } else {
    const x = a.x;
    const ylo = Math.min(a.y, b.y);
    const yhi = Math.max(a.y, b.y);
    for (const r of rects) {
      if (yhi > r.y + EPS && ylo < r.y + r.height - EPS) {
        const d = x <= r.x ? r.x - x : x >= r.x + r.width ? x - (r.x + r.width) : 0;
        if (d < min) min = d;
      }
    }
  }
  return min;
};

// Drop points that lie on a straight run between their neighbours (endpoints kept).
const simplifyCollinear = (pts: Point[]): Point[] => {
  if (pts.length <= 2) return pts;
  const out: Point[] = [pts[0]];
  for (let i = 1; i < pts.length - 1; i++) {
    const prev = out[out.length - 1];
    const cur = pts[i];
    const next = pts[i + 1];
    const sameX = Math.abs(prev.x - cur.x) < EPS && Math.abs(cur.x - next.x) < EPS;
    const sameY = Math.abs(prev.y - cur.y) < EPS && Math.abs(cur.y - next.y) < EPS;
    if (sameX || sameY) continue; // cur is redundant on a straight run
    out.push(cur);
  }
  out.push(pts[pts.length - 1]);
  return out;
};

const uniqSorted = (values: number[]): number[] => {
  const sorted = [...values].sort((a, b) => a - b);
  const out: number[] = [];
  for (const v of sorted) {
    if (out.length === 0 || Math.abs(out[out.length - 1] - v) > EPS) out.push(v);
  }
  return out;
};

// The naive orthogonal path between two points: a horizontal-vertical-horizontal
// zig through the midpoint x (or a straight segment when already aligned).
const naivePath = (from: Point, to: Point): Point[] => {
  if (Math.abs(from.y - to.y) < EPS || Math.abs(from.x - to.x) < EPS) return [from, to];
  const midX = (from.x + to.x) / 2;
  return [from, { x: midX, y: from.y }, { x: midX, y: to.y }, to];
};

const nearestIndex = (values: number[], v: number): number => {
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < values.length; i++) {
    const d = Math.abs(values[i] - v);
    if (d < bestDist) {
      bestDist = d;
      best = i;
    }
  }
  return best;
};

// Binary min-heap keyed by (priority, tie) — lazy: stale entries are pushed on
// decrease-key and skipped on pop. Kept local; the graphs are small but this keeps
// A* at O(E log V) instead of the O(V^2) a linear open-list scan would cost.
class MinHeap {
  private items: Array<{ state: number; f: number }> = [];

  get size(): number {
    return this.items.length;
  }

  // Lower f wins; ties broken by lower state id so routes are deterministic.
  private less(a: { state: number; f: number }, b: { state: number; f: number }): boolean {
    return a.f < b.f - EPS || (Math.abs(a.f - b.f) < EPS && a.state < b.state);
  }

  push(state: number, f: number): void {
    const items = this.items;
    items.push({ state, f });
    let i = items.length - 1;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (!this.less(items[i], items[parent])) break;
      [items[i], items[parent]] = [items[parent], items[i]];
      i = parent;
    }
  }

  pop(): { state: number; f: number } | undefined {
    const items = this.items;
    if (items.length === 0) return undefined;
    const top = items[0];
    const last = items.pop();
    if (last !== undefined && items.length > 0) {
      items[0] = last;
      let i = 0;
      for (;;) {
        const l = 2 * i + 1;
        const r = 2 * i + 2;
        let smallest = i;
        if (l < items.length && this.less(items[l], items[smallest])) smallest = l;
        if (r < items.length && this.less(items[r], items[smallest])) smallest = r;
        if (smallest === i) break;
        [items[i], items[smallest]] = [items[smallest], items[i]];
        i = smallest;
      }
    }
    return top;
  }
}

// Arrival axis of a state: 0 = arrived horizontally, 1 = arrived vertically.
const HORIZONTAL = 0;
const VERTICAL = 1;

// A* over the sparse orthogonal visibility graph (Hanan grid restricted to obstacle
// edges). Lattice cells are identified by their (column, row) index — NOT by rounded
// coordinates — so sub-pixel float positions during a drag can never collapse two
// distinct points onto one identity (which would drop grid nodes and make A* spuriously
// fail). The search state is (cell, arrivalAxis): a turn is charged exactly when the
// axis changes, so the minimum-bend route is guaranteed rather than approximated. The
// start is seeded with its stub axis (so the first turn is charged) and a virtual goal
// charges the final turn into the target handle. Returns the point path, or null.
const routeAStar = (start: Point, goal: Point, startAxis: number, goalAxis: number, obstacles: Rect[], params: RouteParams): Point[] | null => {
  const { margin, hardClearance, bendPenalty, proximityWeight } = params;
  // Hard keep-out for blocking; candidate lines at both the hard offset (hugging boundary)
  // and the preferred offset (comfortable clearance) so A* has a node to sit on for either.
  const hardExpanded = obstacles.map((r) => expandRect(r, hardClearance));
  const xEdges = obstacles.flatMap((r) => [r.x - margin, r.x - hardClearance, r.x + r.width + hardClearance, r.x + r.width + margin]);
  const yEdges = obstacles.flatMap((r) => [r.y - margin, r.y - hardClearance, r.y + r.height + hardClearance, r.y + r.height + margin]);
  const xs = uniqSorted([start.x, goal.x, ...xEdges]);
  const ys = uniqSorted([start.y, goal.y, ...yEdges]);
  const nx = xs.length;
  const ny = ys.length;
  if (nx * ny > MAX_GRID_CELLS) return null;

  const pointAt = (i: number, j: number): Point => ({ x: xs[i], y: ys[j] });
  const cellId = (i: number, j: number): number => i * ny + j;
  const col = (cell: number): number => Math.floor(cell / ny);
  const row = (cell: number): number => cell % ny;
  // A state packs (cell, arrivalAxis) into one integer; VIRTUAL_GOAL terminates the search.
  const stateId = (cell: number, axis: number): number => cell * 2 + axis;
  const stateCell = (state: number): number => Math.floor(state / 2);
  const stateAxis = (state: number): number => state % 2;
  const VIRTUAL_GOAL = -1;

  const blocked: boolean[] = new Array(nx * ny);
  for (let i = 0; i < nx; i++) {
    for (let j = 0; j < ny; j++) blocked[cellId(i, j)] = hardExpanded.some((r) => pointStrictlyInside(pointAt(i, j), r));
  }

  const startCell = cellId(nearestIndex(xs, start.x), nearestIndex(ys, start.y));
  const goalCell = cellId(nearestIndex(xs, goal.x), nearestIndex(ys, goal.y));
  if (blocked[startCell] || blocked[goalCell]) return null;

  const goalX = xs[col(goalCell)];
  const goalY = ys[row(goalCell)];
  const h = (state: number): number => {
    if (state === VIRTUAL_GOAL) return 0;
    const cell = stateCell(state);
    return Math.abs(xs[col(cell)] - goalX) + Math.abs(ys[row(cell)] - goalY);
  };

  const startState = stateId(startCell, startAxis);
  const gScore = new Map<number, number>([[startState, 0]]);
  const cameFrom = new Map<number, number>();
  const heap = new MinHeap();
  heap.push(startState, h(startState));

  const relax = (from: number, to: number, cost: number): void => {
    const tentative = (gScore.get(from) ?? Infinity) + cost;
    if (tentative < (gScore.get(to) ?? Infinity) - EPS) {
      cameFrom.set(to, from);
      gScore.set(to, tentative);
      heap.push(to, tentative + h(to));
    }
  };

  while (heap.size > 0) {
    const popped = heap.pop();
    if (!popped) break;
    const current = popped.state;
    // Skip stale heap entries (lazy decrease-key).
    if (Math.abs(popped.f - ((gScore.get(current) ?? Infinity) + h(current))) > EPS) continue;

    if (current === VIRTUAL_GOAL) {
      const path: Point[] = [];
      let node = cameFrom.get(current); // skip the virtual node; start at the goal cell
      while (node !== undefined) {
        const cell = stateCell(node);
        path.unshift(pointAt(col(cell), row(cell)));
        node = cameFrom.get(node);
      }
      return path;
    }

    const cell = stateCell(current);
    const axis = stateAxis(current);
    const ci = col(cell);
    const cj = row(cell);

    // Reaching the goal cell, step into the target handle: charge the entry turn once.
    if (cell === goalCell) relax(current, VIRTUAL_GOAL, axis !== goalAxis ? bendPenalty : 0);

    const steps: Array<[number, number, number]> = [
      [ci - 1, cj, HORIZONTAL],
      [ci + 1, cj, HORIZONTAL],
      [ci, cj - 1, VERTICAL],
      [ci, cj + 1, VERTICAL],
    ];
    for (const [ni, nj, moveAxis] of steps) {
      if (ni < 0 || ni >= nx || nj < 0 || nj >= ny || blocked[cellId(ni, nj)]) continue;
      const from = pointAt(ci, cj);
      const to = pointAt(ni, nj);
      if (!segmentClear(from, to, hardExpanded)) continue;
      const length = Math.abs(to.x - from.x) + Math.abs(to.y - from.y);
      const bend = moveAxis !== axis ? bendPenalty : 0;
      // Soft proximity: running closer than the preferred margin costs extra, scaled by how
      // close (0 at the margin, up to proximityWeight per pixel at the hard clearance).
      const deficit = Math.max(0, margin - clearanceAlong(from, to, obstacles));
      const proximity = (proximityWeight * deficit * length) / margin;
      relax(current, stateId(cellId(ni, nj), moveAxis), length + bend + proximity);
    }
  }
  return null;
};

// Build an SVG path string through an orthogonal polyline, with rounded corners at
// each turn. Corner radius is capped at half the shorter adjoining segment so short
// segments don't over-round. Straight (2-point) polylines render as a single line.
export const roundedOrthogonalPath = (points: Point[], radius = 8): string => {
  if (points.length === 0) return '';
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  const len = (a: Point, b: Point): number => Math.hypot(b.x - a.x, b.y - a.y);
  const towards = (from: Point, to: Point, d: number): Point => {
    const l = len(from, to) || 1;
    return { x: from.x + ((to.x - from.x) / l) * d, y: from.y + ((to.y - from.y) / l) * d };
  };
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1];
    const cur = points[i];
    const next = points[i + 1];
    const r = Math.min(radius, len(prev, cur) / 2, len(cur, next) / 2);
    const p1 = towards(cur, prev, r);
    const p2 = towards(cur, next, r);
    d += ` L ${p1.x} ${p1.y} Q ${cur.x} ${cur.y} ${p2.x} ${p2.y}`;
  }
  const last = points[points.length - 1];
  d += ` L ${last.x} ${last.y}`;
  return d;
};

const rectsIntersect = (a: Rect, b: Rect): boolean =>
  a.x < b.x + b.width + EPS && b.x < a.x + a.width + EPS && a.y < b.y + b.height + EPS && b.y < a.y + a.height + EPS;

// Smallest axis-aligned box covering all the given rects.
const boundsOfRects = (rects: Rect[]): Rect => {
  const minX = Math.min(...rects.map((r) => r.x));
  const minY = Math.min(...rects.map((r) => r.y));
  const maxX = Math.max(...rects.map((r) => r.x + r.width));
  const maxY = Math.max(...rects.map((r) => r.y + r.height));
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
};

const growToContain = (r: Rect, p: Point): Rect => {
  const minX = Math.min(r.x, p.x);
  const minY = Math.min(r.y, p.y);
  const maxX = Math.max(r.x + r.width, p.x);
  const maxY = Math.max(r.y + r.height, p.y);
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
};

const nearRectBoundary = (p: Point, r: Rect, m: number): boolean => p.x - r.x < m || r.x + r.width - p.x < m || p.y - r.y < m || r.y + r.height - p.y < m;

// Initial slack beyond the endpoint boxes, and how far to widen the scope each time the
// route reaches its edge. Kept generous; the self-correcting loop guarantees correctness.
const SCOPE_PADDING = 96;
const SCOPE_GROWTH = 256;
const MAX_SCOPE_ROUNDS = 4;

// Route one edge. Returns interior waypoints (endpoints excluded), already simplified.
export const routeEdge = (input: RouteInput, params: RouteParams = DEFAULT_ROUTE_PARAMS): Point[] => {
  const { source, target, sourceDir, targetDir, obstacles } = input;
  const sourceStub: Point = { x: source.x + sourceDir.x * params.stubLength, y: source.y + sourceDir.y * params.stubLength };
  const targetStub: Point = { x: target.x + targetDir.x * params.stubLength, y: target.y + targetDir.y * params.stubLength };
  const axisOf = (dir: Point): number => (Math.abs(dir.x) >= Math.abs(dir.y) ? HORIZONTAL : VERTICAL);

  const finish = (stubToStub: Point[]): Point[] => {
    const full = simplifyCollinear([source, sourceStub, ...stubToStub.slice(1, -1), targetStub, target]);
    return full.slice(1, -1); // drop the endpoints
  };

  // Route against a given (scoped) obstacle set. Tier 1: naive stub-to-stub path, used only
  // if every segment clears the boxes AND keeps full preferred clearance (no proximity cost);
  // otherwise Tier 2 A* weighs hugging against a cleaner detour.
  const routeAgainst = (scoped: Rect[]): Point[] => {
    const hardExpanded = scoped.map((r) => expandRect(r, params.hardClearance));
    const tier1 = naivePath(sourceStub, targetStub);
    let comfortable = true;
    for (let i = 0; i < tier1.length - 1 && comfortable; i++) {
      comfortable = segmentClear(tier1[i], tier1[i + 1], hardExpanded) && clearanceAlong(tier1[i], tier1[i + 1], scoped) >= params.margin - EPS;
    }
    if (comfortable) return finish(tier1);
    const routed = routeAStar(sourceStub, targetStub, axisOf(sourceDir), axisOf(targetDir), scoped, params);
    return finish(routed ?? tier1);
  };

  // Scope to obstacles near the edge, seeded from the endpoint node boxes (the obstacle just
  // inside each handle) so the source/target bodies are always included, then padded. If the
  // resulting route reaches the scope edge it may have wanted to avoid something just outside,
  // so widen and re-route — self-correcting, so scoping can never reintroduce a crossing.
  const seedRects = obstacles.filter(
    (o) =>
      pointStrictlyInside({ x: source.x - sourceDir.x, y: source.y - sourceDir.y }, o) ||
      pointStrictlyInside({ x: target.x - targetDir.x, y: target.y - targetDir.y }, o)
  );
  let base = boundsOfRects(seedRects.length ? seedRects : [{ x: source.x, y: source.y, width: 0, height: 0 }]);
  for (const p of [source, target, sourceStub, targetStub]) base = growToContain(base, p);

  let padding = SCOPE_PADDING;
  let result: Point[] = [];
  for (let round = 0; ; round++) {
    const scopeRect = expandRect(base, padding);
    const scoped = obstacles.filter((o) => rectsIntersect(o, scopeRect));
    result = routeAgainst(scoped);
    const reachedEdge = [source, ...result, target].some((p) => nearRectBoundary(p, scopeRect, params.margin));
    if (!reachedEdge || scoped.length === obstacles.length || round >= MAX_SCOPE_ROUNDS) break;
    padding += SCOPE_GROWTH;
  }
  return result;
};
